import { forwardRef, useEffect, useRef, useState } from "react"
import { NumericFormat, NumberFormatValues } from "react-number-format"
import { APP_DEFAULT_DECIMAL_PLACE } from "config"
import {
  hasStraySecondDecimalSeparator,
  isDecimalSeparatorPrepended,
  isNoOpAfterNormalization,
  moveCaretAfterDecimal,
  normalizeNumericInputValue,
} from "utils/decimalInput"
import { StyledPortionInput } from "./styled"

export interface IPortionInputProps {
  value: number
  defaultValue?: number
  width?: number
  onChange: (value: number) => void
  disabled?: boolean
  setIsPortionValid?: (isValid: boolean) => void
}

type Ref = HTMLInputElement

const PortionInput = forwardRef<Ref, IPortionInputProps>((props, ref) => {
  const {
    value,
    defaultValue = 1,
    width,
    disabled = false,
    onChange,
    setIsPortionValid,
  } = props
  const [internalValue, setInternalValue] = useState<string>(value.toString())
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const pendingDecimalResetRef = useRef(false)

  useEffect(() => {
    if (value !== Number(internalValue)) {
      setInternalValue(value.toString())
    }
  }, [value])

  useEffect(() => () => clearTimeout(blurTimeoutRef.current), [])

  const onValueChange = (values: NumberFormatValues) => {
    if (disabled) {
      return
    }

    if (pendingDecimalResetRef.current) {
      pendingDecimalResetRef.current = false
      setInternalValue(".")
      setIsPortionValid?.(false)
      setTimeout(() => moveCaretAfterDecimal((ref as any).current), 20)
      return
    }

    const parsedValue = values.floatValue ?? 0

    const normalized = normalizeNumericInputValue(values.value)
    const isValidPortion = normalized !== "" && parsedValue > 0
    
    setIsPortionValid?.(isValidPortion)

    if (isValidPortion && values.floatValue !== value) {
      onChange?.(parsedValue)
    }

    setInternalValue(normalized)
  }

  const onBlur = () => {
    clearTimeout(blurTimeoutRef.current)
    blurTimeoutRef.current = setTimeout(() => {
      const nextValue = !!value && value > 0 ? value : defaultValue
      if (internalValue !== nextValue.toString()) {
        setInternalValue(nextValue.toString())
        onChange(nextValue)
      }
      setIsPortionValid?.(true)
    }, 100)
  }

  const isAllowed = (values: NumberFormatValues) => {
    pendingDecimalResetRef.current = false

    const rawValue = (ref as any)?.current?.value ?? values.value

    if (isDecimalSeparatorPrepended(rawValue, internalValue)) {
      pendingDecimalResetRef.current = true
      return true
    }

    if (hasStraySecondDecimalSeparator(rawValue, internalValue)) {
      return false
    }

    const normalized = normalizeNumericInputValue(values.value)
    return !isNoOpAfterNormalization(values.value, normalized, internalValue)
  }

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (internalValue === defaultValue.toString()) {
      const target = e.target
      setTimeout(() => target.select(), 0)
    }
  }

  return (
    <NumericFormat
      customInput={StyledPortionInput}
      $width={width}
      getInputRef={ref}
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      decimalScale={APP_DEFAULT_DECIMAL_PLACE}
      allowNegative={false}
      thousandSeparator={","}
      decimalSeparator="."
      allowedDecimalSeparators={[",", "."]}
      isAllowed={isAllowed}
      value={internalValue}
      valueIsNumericString
      onFocus={onFocus}
      onBlur={onBlur}
      onValueChange={onValueChange}
    />
  )
})

export default PortionInput
